import { DeleteObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { putCaptionSegment, putDescriptionSegment, putScriptSegmentSign } from "../descriptions/description";
import { process } from "../env";
import { deleteRecord } from "../regions/deleteRecord";


export const uploadVideoVoiceOver = async (file) => {
  try {
    if (!file) {
      console.error("❌ No file selected");
      return { error: "No file selected" };
    }

    console.log("📤 Preparing voice-over video upload...");

    // Usar el nuevo endpoint del backend
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${process.env.REACT_APP_URL}/api/uploads/voice-over-video`, {
      method: 'POST',
      body: formData
    });

    const result = await response.json();

    if (result.status) {
      console.log("✅ Voice-over video uploaded to S3:", result.data.nombreUnico);
      return result.data.location; // Mantiene la compatibilidad con el código original
    } else {
      throw new Error(`Error uploading voice-over video: ${result.msg}`);
    }
  } catch (error) {
    console.error("🔥 Error in uploadVideoVoiceOver:", error);
    return error; // Mantiene la compatibilidad con el código original
  }
};

export const upladRecording = async (project, id, file) => {
  try {
    if (!file) {
      console.error("❌ No file selected");
      return { error: "No file selected" };
    }

    if (!project || !id) {
      console.error("❌ Project and ID are required");
      return { error: "Project and ID are required" };
    }

    console.log("📤 Preparing recording upload...");

    // Usar el nuevo endpoint del backend
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project', project);
    formData.append('id', id);

    const response = await fetch(`${process.env.REACT_APP_URL}/api/uploads/recording`, {
      method: 'POST',
      body: formData
    });

    const result = await response.json();

    if (result.status) {
      console.log("✅ Recording uploaded to S3:", result.data.nombreUnico);
      console.log("✅ Segment updated with audio record URL");
      
      // El backend ya actualizó el segmento automáticamente
      // No necesitas llamar a putDescriptionSegment manualmente
      
      return { success: true, url: result.data.url };
    } else {
      throw new Error(`Error uploading recording: ${result.msg}`);
    }
  } catch (error) {
    console.error("🔥 Error in upladRecording:", error);
    return error; // Mantiene la compatibilidad con el código original
  }
};
export const upladRecordingVideo = async (region, id, file) => {
  try {
    if (!file) {
      console.error("❌ No file selected");
      return { error: "No file selected" };
    }

    if (!region || !id) {
      console.error("❌ Region and ID are required");
      return { error: "Region and ID are required" };
    }

    console.log("📤 Preparing recording video upload...");

    // Extraer solo los datos necesarios del objeto region para evitar referencias circulares
    const regionData = {
      start: region.start,
      end: region.end,
      id: region.data?.id,
      description: region.data?.description,
      audiorecord: region.data?.audiorecord,
      comment: region.data?.comment,
      review: region.data?.review,
      user: region.data?.user
    };

    // Usar el endpoint correcto del backend
    const formData = new FormData();
    formData.append('file', file);
    formData.append('region', JSON.stringify(regionData));
    formData.append('id', id);

    const response = await fetch(`${process.env.REACT_APP_URL}/api/uploads/recording`, {
      method: 'POST',
      body: formData
    });

    const result = await response.json();

    if (result.status) {
      console.log("✅ Recording video uploaded to S3:", result.data.nombreUnico);
      console.log("✅ Segment updated with video record URL");
      
      // El backend ya:
      // 1. Calculó la versión automáticamente
      // 2. Subió el video a S3
      // 3. Eliminó el video anterior
      // 4. Actualizó el segmento automáticamente
      
      return result.data.url; // Mantiene la compatibilidad con el código original
    } else {
      throw new Error(`Error uploading recording video: ${result.msg}`);
    }
  } catch (error) {
    console.error("🔥 Error in upladRecordingVideo:", error);
    return error; // Mantiene la compatibilidad con el código original
  }
};

export const upladRecordingDubbing = async (region, id, userId, file) => {
  try {
    if (!file) {
      console.error("❌ No file selected");
      return { error: "No file selected" };
    }

    if (!region || !id || !userId) {
      console.error("❌ Region, ID and userId are required");
      return { error: "Region, ID and userId are required" };
    }

    console.log("📤 Preparing dubbing recording upload...");

    // Extraer solo los datos necesarios del objeto region para evitar referencias circulares
    const regionData = {
      start: region.start,
      end: region.end,
      id: region.data?.id,
      description: region.data?.description,
      audiorecord: region.data?.audiorecord,
      comment: region.data?.comment,
      review: region.data?.review,
      user: region.data?.user
    };

    // Usar el endpoint correcto del backend
    const formData = new FormData();
    formData.append('file', file);
    formData.append('region', JSON.stringify(regionData));
    formData.append('id', id);
    formData.append('userId', userId);

    const response = await fetch(`${process.env.REACT_APP_URL}/api/uploads/recording`, {
      method: 'POST',
      body: formData
    });

    const result = await response.json();

    if (result.status) {
      console.log("✅ Dubbing recording uploaded to S3:", result.data.nombreUnico);
      console.log("✅ Segment updated with audio record URL");
      
      // El backend ya:
      // 1. Calculó la versión automáticamente
      // 2. Subió el audio a S3
      // 3. Eliminó el audio anterior
      // 4. Actualizó el segmento automáticamente con initial y preview
      
      return result.data.audiorecord; // Mantiene la compatibilidad con el código original
    } else {
      throw new Error(`Error uploading dubbing recording: ${result.msg}`);
    }
  } catch (error) {
    console.error("🔥 Error in upladRecordingDubbing:", error);
    return error; // Mantiene la compatibilidad con el código original
  }
};
export const uploadRecordingAudio = async (region, id, file) => {
  try {
    if (!file) {
      console.error("❌ No file selected");
      return { error: "No file selected" };
    }

    if (!region || !id) {
      console.error("❌ Region and ID are required");
      return { error: "Region and ID are required" };
    }

    console.log("📤 Preparing audio recording upload...");
    console.log("�� Original region:", region);

    // Extraer solo los datos necesarios del objeto region para evitar referencias circulares
    const regionData = {
      start: region.start,
      end: region.end,
      id: region.data?.id,
      description: region.data?.description,
      audiorecord: region.data?.audiorecord,
      comment: region.data?.comment,
      review: region.data?.review,
      user: region.data?.user,
      project: region.data?.project // Asegúrate de incluir el proyecto
    };

    console.log("📦 Processed region data:", regionData);

    // Usar la ruta correcta
    const formData = new FormData();
    formData.append('file', file);
    formData.append('region', JSON.stringify(regionData));
    formData.append('id', id);

    console.log("🌐 Making request to:", `${process.env.REACT_APP_URL}/api/uploads/recording-audio`);

    const response = await fetch(`${process.env.REACT_APP_URL}/api/uploads/recording-audio`, {
      method: 'POST',
      body: formData
    });

    console.log("�� Response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Server error:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("📦 Response data:", result);

    if (result.status) {
      console.log("✅ Audio recording uploaded to S3:", result.data.nombreUnico);
      console.log("✅ Segment updated with audio record URL");
      
      return result.data.audiorecord;
    } else {
      throw new Error(`Error uploading audio recording: ${result.msg}`);
    }
  } catch (error) {
    console.error("🔥 Error in uploadRecordingAudio:", error);
    return error;
  }
};